import { useUserRewardsClaim } from '@/api/user-rewards';
import { RewardId } from '@/enums/reward-id';
import { UserRewardStatusId } from '@/models/user-reward/status/id';
import { getContrastColor } from '@/utils/color/getContrastColor';
import { Button, Counter, ToolTip } from '@starsoft/common/components';
import { useIsMobile } from '@starsoft/common/hooks';
import { WalletService } from '@starsoft/common/services';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import useTranslation from 'next-translate/useTranslation';
import { RewardSubmitButtonProps } from './props';
import styles from './styles.module.scss';

export default function RewardSubmitButton({
  userReward,
  reward,
  countdown,
  dynamicReward,
  isLoading,
}: RewardSubmitButtonProps) {
  const isMobile = useIsMobile();
  const { claimUserReward, isLoading: isLoadingClaimUserReward } =
    useUserRewardsClaim();

  const { t } = useTranslation('bonus');
  const { hours, minutes, seconds, isCounting } = countdown ?? {};
  const isSkeleton = !userReward;

  const isLevelUp =
    userReward?.reward?.id?.toString() === RewardId.LevelUp.toString();

  function getAmount() {
    return getMoneyOrAmount(userReward?.amount);
  }

  const isLessThanMinimum =
    (getAmount() ?? 0) < getMoneyOrAmount(userReward?.reward?.minimum) &&
    !isLevelUp;

  const isDisabled =
    !userReward ||
    (!!userReward &&
      [
        UserRewardStatusId.Cancelled.toString(),
        UserRewardStatusId.Expired.toString(),
        UserRewardStatusId.Locked.toString(),
        UserRewardStatusId.Claimed.toString(),
      ].includes(userReward?.reward?.statusId?.toString())) ||
    isLessThanMinimum;

  function getLabel() {
    switch (userReward?.reward?.statusId?.toString()) {
      case UserRewardStatusId.Cancelled.toString():
      case UserRewardStatusId.Expired.toString():
      case UserRewardStatusId.Locked.toString():
      case UserRewardStatusId.Claimed.toString():
        return t('redeemableAt_description', {
          hours,
          minutes,
          seconds,
        });
      case UserRewardStatusId.Available.toString():
      case UserRewardStatusId.InProgress.toString():
        return t('claim_label');
    }
  }

  function handleRedeemReward() {
    if (!userReward?.id) {
      return;
    }

    claimUserReward({ userRewardId: userReward?.id });
  }

  if (
    isCounting &&
    userReward?.statusId?.toString() !== UserRewardStatusId.Available.toString()
  ) {
    return (
      <Counter
        countdown={countdown}
        isSmall
        hideDays
        hideHours={countdown?.hours === '00'}
      />
    );
  }

  if (isLoading) {
    return (
      <Button
        type="button"
        isSmall={!isMobile}
        useMaxContent={!isMobile}
        isSkeleton
      />
    );
  }

  return (
    <>
      <ToolTip
        visible={
          isLessThanMinimum &&
          !isLevelUp &&
          userReward?.statusId?.toString() !==
            UserRewardStatusId.Available.toString()
        }
        titleComponent={t('minimum_reward_description', {
          value: WalletService.maskCurrency({
            amount: getMoneyOrAmount(userReward?.reward?.minimum),
            coin: dynamicReward?.coin,
          }),
        })}
      >
        <div>
          <Button
            type="button"
            onClick={handleRedeemReward}
            isloading={isLoadingClaimUserReward}
            isSecondary={!userReward}
            disabled={isDisabled}
            color={reward?.color}
            isSmall={!isMobile}
            useMaxContent={!isMobile}
            textColor={
              reward?.color ? getContrastColor(reward?.color) : undefined
            }
            isSkeleton={!reward || isSkeleton}
            className={isDisabled ? styles.button : ''}
          >
            {!!userReward &&
            userReward?.status?.id?.toString() ===
              UserRewardStatusId.Locked.toString() ? (
              <div className={styles.row}>
                <i className="fa-solid fa-clock" />
                {getLabel()}
              </div>
            ) : (
              getLabel()
            )}
          </Button>
        </div>
      </ToolTip>
      {isLessThanMinimum &&
        userReward?.status?.id?.toString() ===
          UserRewardStatusId.Available.toString() &&
        isMobile && (
          <div className={`${styles.row__tooltip}`}>
            <p>
              {t('minimum_reward_description', {
                value: WalletService.maskCurrency({
                  amount: getMoneyOrAmount(userReward?.reward?.minimum),
                  coin: dynamicReward?.coin,
                }),
              })}
            </p>
          </div>
        )}
    </>
  );
}
